import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import './ShopifyAuditPage.css';

function formatTs(value) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function renderJson(value) {
  if (!value) return '—';
  try {
    return JSON.stringify(value, null, 2);
  } catch (_) {
    return String(value);
  }
}

function classifyOwnerChangeSource(row = {}) {
  const source = String(row.source || row.functionName || '').trim();
  const rowProductType = String(row.productType || '').trim();
  const conflictingOwners = Array.isArray(row.details?.conflictingOwners) ? row.details.conflictingOwners : [];
  const hasCrossTypeCollision = rowProductType && conflictingOwners.some((owner) => {
    const ownerType = String(owner?.productType || '').trim();
    return ownerType && ownerType !== rowProductType;
  });
  if (hasCrossTypeCollision) {
    return {
      key: 'cross_type_sku_collision',
      label: 'Cross-Type SKU Collision',
      description: 'The same SKU exists under more than one live product type. Resolve publish ownership using SKU plus product type, not SKU alone.',
      pillClass: 'warning',
    };
  }
  if (!source) {
    return {
      key: 'unknown',
      label: 'Source unclear',
      description: 'The audit row does not contain enough source data to attribute the owner change.',
      pillClass: 'unknown',
    };
  }
  if (source === 'autoSyncProducts') {
    return {
      key: 'external_detected',
      label: 'Detected externally via sync',
      description: 'The owner change was first observed when Shopify products were mirrored into Firestore. No audited internal write helper is linked to this row.',
      pillClass: 'warning',
    };
  }
  return {
    key: 'internal_write',
    label: 'Created by internal write',
    description: 'The owner change is attached to an audited backend Shopify write helper in this codebase.',
    pillClass: 'success',
  };
}

function readOwnerChange(row = {}) {
  const oldProductId = row.oldProductId || row.beforeSummary?.product_id || row.details?.oldProductId || null;
  const newProductId = row.newProductId || row.responseSummary?.product_id || row.details?.newProductId || null;
  const oldVariantId = row.oldVariantId || row.beforeSummary?.id || row.details?.oldVariantId || null;
  const newVariantId = row.newVariantId || row.responseSummary?.id || row.details?.newVariantId || null;
  const changedProduct = Boolean(oldProductId && newProductId && String(oldProductId) !== String(newProductId));
  const changedVariant = Boolean(oldVariantId && newVariantId && String(oldVariantId) !== String(newVariantId));
  if (!changedProduct && !changedVariant) return null;
  const classification = classifyOwnerChangeSource(row);
  return {
    sku: row.sku || row.details?.sku || row.requestSummary?.sku || '—',
    source: row.source || row.functionName || 'unknown',
    createdAt: row.createdAt || null,
    oldProductId,
    newProductId,
    oldVariantId,
    newVariantId,
    changedProduct,
    changedVariant,
    title: row.title || row.kind || row.functionName || 'Owner change',
    classification,
    raw: row,
  };
}

export default function ShopifyAuditPage() {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loadingDuplicates, setLoadingDuplicates] = useState(false);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);
  const [recentWrites, setRecentWrites] = useState([]);
  const [recentAlerts, setRecentAlerts] = useState([]);
  const [duplicates, setDuplicates] = useState([]);
  const [productTypeFilter, setProductTypeFilter] = useState('Digital Printed Fabric');

  const authedFetch = useCallback(async (url) => {
    if (!currentUser) throw new Error('Not authenticated.');
    const token = await currentUser.getIdToken();
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const text = await response.text();
    let data = {};
    try {
      data = text ? JSON.parse(text) : {};
    } catch (_) {
      throw new Error(`Invalid server response from ${url}`);
    }
    if (!response.ok || data?.success === false) {
      throw new Error(data?.message || `Request failed for ${url}`);
    }
    return data;
  }, [currentUser]);

  const loadSummary = useCallback(async () => {
    const data = await authedFetch('/api/v1/admin/shopify-audit/summary?days=7&limit=50');
    setSummary(data.summary || null);
    setRecentWrites(Array.isArray(data.recentWrites) ? data.recentWrites : []);
    setRecentAlerts(Array.isArray(data.recentAlerts) ? data.recentAlerts : []);
  }, [authedFetch]);

  const loadDuplicates = useCallback(async () => {
    setLoadingDuplicates(true);
    try {
      const qs = new URLSearchParams({
        limit: '100',
        sameTypeOnly: 'true',
      });
      if (String(productTypeFilter || '').trim()) qs.set('productType', String(productTypeFilter || '').trim());
      const data = await authedFetch(`/api/v1/admin/shopify-audit/duplicates?${qs.toString()}`);
      setDuplicates(Array.isArray(data.duplicates) ? data.duplicates : []);
    } finally {
      setLoadingDuplicates(false);
    }
  }, [authedFetch, productTypeFilter]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      await Promise.all([loadSummary(), loadDuplicates()]);
    } catch (err) {
      setError(err?.message || 'Failed to load Shopify audit.');
    } finally {
      setLoading(false);
    }
  }, [loadDuplicates, loadSummary]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const visibleWrites = useMemo(() => recentWrites.slice(0, 30), [recentWrites]);
  const visibleAlerts = useMemo(() => recentAlerts.slice(0, 30), [recentAlerts]);
  const ownerChanges = useMemo(() => {
    const seen = new Set();
    const rows = [];
    [...recentAlerts, ...recentWrites].forEach((row) => {
      const change = readOwnerChange(row);
      if (!change) return;
      const key = [
        change.sku,
        change.oldProductId || '',
        change.newProductId || '',
        change.oldVariantId || '',
        change.newVariantId || '',
        formatTs(change.createdAt),
      ].join('|');
      if (seen.has(key)) return;
      seen.add(key);
      rows.push(change);
    });
    rows.sort((a, b) => {
      const aMs = new Date(a.createdAt || 0).getTime();
      const bMs = new Date(b.createdAt || 0).getTime();
      return (Number.isFinite(bMs) ? bMs : 0) - (Number.isFinite(aMs) ? aMs : 0);
    });
    return rows.slice(0, 30);
  }, [recentAlerts, recentWrites]);
  const ownerChangeSummary = useMemo(() => ownerChanges.reduce((acc, row) => {
    const key = row.classification?.key || 'unknown';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {}), [ownerChanges]);

  return (
    <div className="shopify-audit-page">
      <div className="shopify-audit-header">
        <div>
          <h1>Shopify Audit</h1>
          <p>Tracks product and variant write behavior so ID drift and duplicate owner chains become visible quickly.</p>
        </div>
        <button type="button" className="shopify-audit-refresh" onClick={loadAll} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {error ? <div className="shopify-audit-error">{error}</div> : null}

      <div className="shopify-audit-summary-grid">
        <div className="shopify-audit-card">
          <div className="shopify-audit-kicker">Writes (7d)</div>
          <div className="shopify-audit-value">{summary?.writeCount ?? '—'}</div>
        </div>
        <div className="shopify-audit-card">
          <div className="shopify-audit-kicker">Failed Writes</div>
          <div className="shopify-audit-value">{summary?.failedWriteCount ?? '—'}</div>
        </div>
        <div className="shopify-audit-card">
          <div className="shopify-audit-kicker">Identity Drift</div>
          <div className="shopify-audit-value">{summary?.identityDriftCount ?? '—'}</div>
        </div>
        <div className="shopify-audit-card">
          <div className="shopify-audit-kicker">Response Mismatch</div>
          <div className="shopify-audit-value">{summary?.mismatchCount ?? '—'}</div>
        </div>
        <div className="shopify-audit-card">
          <div className="shopify-audit-kicker">Alerts</div>
          <div className="shopify-audit-value">{summary?.alertCount ?? '—'}</div>
        </div>
        <div className="shopify-audit-card">
          <div className="shopify-audit-kicker">Owner Changes</div>
          <div className="shopify-audit-value">{ownerChanges.length}</div>
        </div>
        <div className="shopify-audit-card">
          <div className="shopify-audit-kicker">Internal Write Changes</div>
          <div className="shopify-audit-value">{ownerChangeSummary.internal_write || 0}</div>
        </div>
        <div className="shopify-audit-card">
          <div className="shopify-audit-kicker">Externally Detected</div>
          <div className="shopify-audit-value">{ownerChangeSummary.external_detected || 0}</div>
        </div>
        <div className="shopify-audit-card">
          <div className="shopify-audit-kicker">Cross-Type Collisions</div>
          <div className="shopify-audit-value">{ownerChangeSummary.cross_type_sku_collision || 0}</div>
        </div>
      </div>

      <div className="shopify-audit-panel">
        <div className="shopify-audit-panel-header">
          <h2>Owner Changes</h2>
        </div>
        <div className="shopify-audit-caption">
          Shows only cases where a SKU moved to a different Shopify product id or variant id.
        </div>
        {ownerChanges.length === 0 ? (
          <div className="shopify-audit-empty">No owner changes detected in the recent audit window.</div>
        ) : (
          <div className="shopify-audit-owner-list">
            {ownerChanges.map((row, index) => (
              <details key={`${row.sku}-${index}`} className="shopify-audit-entry">
                <summary>
                  <div>
                    <strong>{row.sku}</strong>
                    <div className="shopify-audit-meta">{row.source} · {formatTs(row.createdAt)}</div>
                  </div>
                  <div className="shopify-audit-owner-summary">
                    <span className={`shopify-audit-pill shopify-audit-pill-${row.classification?.pillClass || 'unknown'}`}>
                      {row.classification?.label || 'Source unclear'}
                    </span>
                    <span className="shopify-audit-pill shopify-audit-pill-warning">owner changed</span>
                  </div>
                </summary>
                <div className="shopify-audit-owner-description">
                  {row.classification?.description || 'Source attribution unavailable.'}
                </div>
                <div className="shopify-audit-owner-grid">
                  <div className="shopify-audit-owner-cell">
                    <div className="shopify-audit-owner-label">Product</div>
                    <div className="shopify-audit-owner-change">
                      <code>{row.oldProductId || '—'}</code>
                      <span>→</span>
                      <code>{row.newProductId || '—'}</code>
                    </div>
                  </div>
                  <div className="shopify-audit-owner-cell">
                    <div className="shopify-audit-owner-label">Variant</div>
                    <div className="shopify-audit-owner-change">
                      <code>{row.oldVariantId || '—'}</code>
                      <span>→</span>
                      <code>{row.newVariantId || '—'}</code>
                    </div>
                  </div>
                </div>
                <div className="shopify-audit-body">
                  <pre>{renderJson(row.raw)}</pre>
                </div>
              </details>
            ))}
          </div>
        )}
      </div>

      <div className="shopify-audit-panel">
        <div className="shopify-audit-panel-header">
          <h2>Duplicate SKU Scan</h2>
          <div className="shopify-audit-filter-bar">
            <input
              type="text"
              value={productTypeFilter}
              onChange={(e) => setProductTypeFilter(e.target.value)}
              placeholder="Filter by product type"
            />
            <button type="button" onClick={loadDuplicates} disabled={loadingDuplicates}>
              {loadingDuplicates ? 'Scanning…' : 'Scan'}
            </button>
          </div>
        </div>
        <div className="shopify-audit-caption">
          Current Firestore mirror view of SKUs mapped to more than one Shopify product owner.
        </div>
        <div className="shopify-audit-duplicate-list">
          {duplicates.length === 0 ? (
            <div className="shopify-audit-empty">No duplicate SKU owners found for the current filter.</div>
          ) : duplicates.map((row) => (
            <details key={row.sku} className="shopify-audit-duplicate-item">
              <summary>
                <span>{row.sku}</span>
                <span>{row.productType || 'Unknown Type'}</span>
                <span>{row.ownerCount} owners</span>
              </summary>
              <pre>{renderJson(row.owners)}</pre>
            </details>
          ))}
        </div>
      </div>

      <div className="shopify-audit-columns">
        <section className="shopify-audit-panel">
          <div className="shopify-audit-panel-header">
            <h2>Recent Alerts</h2>
          </div>
          {visibleAlerts.length === 0 ? (
            <div className="shopify-audit-empty">No recent alerts.</div>
          ) : visibleAlerts.map((row) => (
            <details key={row.id} className="shopify-audit-entry">
              <summary>
                <div>
                  <strong>{row.title || row.kind || 'Alert'}</strong>
                  <div className="shopify-audit-meta">{row.source || 'unknown'} · {formatTs(row.createdAt)}</div>
                </div>
                <span className={`shopify-audit-pill shopify-audit-pill-${row.severity || 'warning'}`}>{row.severity || 'warning'}</span>
              </summary>
              <div className="shopify-audit-body">
                <div>{row.message || '—'}</div>
                <pre>{renderJson(row.details || row)}</pre>
              </div>
            </details>
          ))}
        </section>

        <section className="shopify-audit-panel">
          <div className="shopify-audit-panel-header">
            <h2>Recent Writes</h2>
          </div>
          {visibleWrites.length === 0 ? (
            <div className="shopify-audit-empty">No recent writes.</div>
          ) : visibleWrites.map((row) => (
            <details key={row.id} className="shopify-audit-entry">
              <summary>
                <div>
                  <strong>{row.functionName || row.source || 'Shopify write'}</strong>
                  <div className="shopify-audit-meta">
                    {row.operation || 'write'} · {formatTs(row.createdAt)} · {row.sku || row.targetVariantId || row.targetProductId || '—'}
                  </div>
                </div>
                <span className={`shopify-audit-pill shopify-audit-pill-${row.status || 'unknown'}`}>{row.status || 'unknown'}</span>
              </summary>
              <div className="shopify-audit-body">
                <pre>{renderJson({
                  source: row.source,
                  route: row.route,
                  triggeredBy: row.triggeredBy,
                  targetProductId: row.targetProductId,
                  targetVariantId: row.targetVariantId,
                  requestSummary: row.requestSummary,
                  beforeSummary: row.beforeSummary,
                  responseSummary: row.responseSummary,
                  identityDrift: row.identityDrift,
                  fieldMismatch: row.fieldMismatch,
                  errorMessage: row.errorMessage,
                })}</pre>
              </div>
            </details>
          ))}
        </section>
      </div>
    </div>
  );
}
